/**
=========================================================
* Material Dashboard 2 PRO React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import client from "ApiClient";
// react-github-btn
// @mui material components
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

import TimezoneSelect from "react-timezone-select";
// @mui icons

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Custom styles for the Configurator
import ConfiguratorRoot from "examples/Configurator/ConfiguratorRoot";

// Material Dashboard 2 PRO React context
import { useMaterialUIController, setOpenConfigurator } from "context";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { CircularProgress, IconButton, Tooltip } from "@material-ui/core";

function Configurator() {
  const [isLoading, setIsLoading] = useState(false);
  const [isValidApiKey, setIsValidApiKey] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [oldApiKey, setOldApiKey] = useState("");
  const [setId, setSetId] = useState("");
  const [userId, setUserId] = useState("");
  const [selectedTimezone, setSelectedTimezone] = useState("");
  const [controller, dispatch] = useMaterialUIController();
  const { openConfigurator, darkMode } = controller;
  const [disabled, setDisabled] = useState(false);

  // Use the useEffect hook to change the button state for the sidenav type based on window size.
  useEffect(() => {
    if (localStorage.getItem("AuthorizationToken") == null) return;
    getData();
    // A function that sets the disabled state of the buttons for the sidenav type.
    function handleDisabled() {
      return window.innerWidth > 1200 ? setDisabled(false) : setDisabled(true);
    }

    // The event listener that's calling the handleDisabled function when resizing the window.
    window.addEventListener("resize", handleDisabled);

    // Call the handleDisabled function to set the state with the initial value.
    handleDisabled();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleDisabled);
  }, []);

  const handleCloseConfigurator = () => setOpenConfigurator(dispatch, false);

  const options = {
    method: "GET",
    url: "settings",
    headers: {
      "content-type": "application/json",
    },
  };

  const getData = async () => {
    setIsLoading(true);
    options.method = "GET";
    options.url = `settings`;
    await client
      .request(options)
      .then((response) => {
        localStorage.setItem("settings", JSON.stringify(response));
        setOldApiKey(response.set_msApiKey);
        setApiKey(response.set_msApiKey);
        setSetId(response.set_id);
        setUserId(response.set_user_id);
        setSelectedTimezone(response.set_timezone);
        setIsLoading(false);
      })
      .catch((error) => {
        // eslint-disable-next-line
        console.log(error);
      });
  };

  const testApiKey = async () => {
    if (apiKey == "" || apiKey == oldApiKey) handleCloseConfigurator();
    options.method = "GET";
    options.url = `forms`;
    await client
      .request(options)
      .then((response) => {
        if (typeof(response.error_message) === "undefined") handleCloseConfigurator();
        else setIsValidApiKey("Invalid API key");
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleSave = async () => {
    setIsValidApiKey("");
    setIsLoading(true);
    options.method = "PUT";
    options.url = `settings`;
    options.data = JSON.stringify({
      set_id: setId,
      set_user_id: userId,
      set_timezone: selectedTimezone,
      set_msApiKey: apiKey,
    });
    await client
      .request(options)
      .then(async (response) => {
        setIsLoading(false);
        localStorage.setItem("settings",  JSON.stringify(response));
        testApiKey();
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <ConfiguratorRoot variant="permanent" ownerState={{ openConfigurator }}>
      <MDBox display="flex" justifyContent="space-between" alignItems="baseline" pt={4} pb={5}>
        <MDBox>
          <MDTypography variant="h4">Settings</MDTypography>
        </MDBox>

        <Icon
          sx={({ typography: { size }, palette: { dark, white } }) => ({
            fontSize: `${size.lg} !important`,
            color: darkMode ? white.main : dark.main,
            stroke: "currentColor",
            strokeWidth: "2px",
            cursor: "pointer",
            transform: "translateY(5px)",
          })}
          onClick={handleCloseConfigurator}
        >
          close
        </Icon>
      </MDBox>

      <MDTypography variant="body1">SendPad API key</MDTypography>
      <MDInput
        readOnly
        type={"text"}
        value={apiKey}
        onChange={(e) => setApiKey(e.target.value)}
        fullWidth
      />
      <MDBox style={{ marginBottom: 20 }} color="red">
        {isValidApiKey}
      </MDBox>
      <MDTypography variant="body1">Select your time zone</MDTypography>
      <TimezoneSelect value={selectedTimezone} onChange={(e) => setSelectedTimezone(e.value)} />
      <MDButton
        onClick={handleSave}
        color="success"
        variant="gradient"
        style={{ marginTop: 20 }}
        disabled={isLoading}
      >
        Save
        {isLoading && <CircularProgress></CircularProgress>}
      </MDButton>
      <Divider />

      <MDButton
        onClick={() =>
          (window.location.href =
            "https://dl.dropboxusercontent.com/s/me3ie6ejja2n0ie/Commission%20Theme.zip?dl=0")
        }
        color="success"
        variant="gradient"
        style={{ marginTop: 20 }}
      >
        Download commision theme<Icon>download</Icon>
      </MDButton>
    </ConfiguratorRoot>
  );
}

export default Configurator;
